import React, { ReactNode, useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Link,
  Modal,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { saveAs } from "file-saver";
import { nanoid } from 'nanoid';
import { useNavigate } from "react-router-dom";
import { isEmpty } from "lodash";

import {
  AuthDetailsBox,
  AuthDetailsGrid,
  NtuButton,
  NoMnCButton,
  displayButton,
  ReAuthButton,
  ReferralName,
  ViewAuthHistoryGrid,
  ViewAuthHistoryButton,
  TableContainerStyle,
  HeaderTableCell,
  StyledTableRow,
  TotalDisciplineGrid,
  DisciplineGrid,
  CloseButton,
  FiAlertCircleIcon,
  UnavailibilityContainer,
  UnavailabilityMessage,
  DocumentContainer,
  DocumentTableContainerStyle,
  DocumentTableHead,
  DocumentNameUnderline,
  CloseButtonGrid,
  FaRegFilePdfIcon,
  DocumentModalTableRow,
  SocButton,
} from "../style/style";
import fontWeight from "../../../styles/mui/fontWeight";
import { displayFlex, displayFlexEnd, flexAllCentre } from "../../../styles/mui/styles/display";
import {
  ApplicationTitles,
  AuthHistoryHeaderLabel,
  DocumentReviewHeaderLabel,
  GetReferralAuthHistoryActionDispatchTypes,
} from "../../../constants/HomePage";
import {
  BlobDocument,
  DocumentReviewList,
  DocumentReviewPayload,
  MaxAllowedSocDate,
  MaxAllowedSocDatePayload,
  ReferralAuthHistory,
  ReferralDetail,
  UpdateStartOfCareDatePayload,
} from "../../../models/Referral";
import { formatDate } from "../../../utils/DateTime";
import { DateFormats } from "../../../constants/Date";
import { colors } from "../../../styles/colors";
import { bytesToKB, encryptId, getCurrnetPageFullPath, getSessionStorage, getUaserPageViewPayload, getValue } from "../../../utils";
import NtuPatient from "../containers/NtuPatient";
import { defaultReferralInfo } from "../../../reducers/ReferralList";
import { ApplicationRoute, ApplicationURL } from "../../../constants/ApplicationUrl";
import { ReAuthDefaultValueActionDispatchTypes, supportedFileTypes } from "../../../constants/Patient";
import { ButtonContained } from "../../../app/common/button";
import { FaHeadsetIcon } from "../../../app/styles/styles";
import { getValidMessageType, REAUTH_UNAVAILIBILITY_ERROR_MESSAGE } from "../../../constants/User";
import toastType from "../../../utils/SnackbarProvider";
import { MessageType, ResponseMessages } from "../../../constants/ResponseMessage";
import {AuthNotificationsDownloadResponse, AuthNotificationsListResponse, DocumentDownloadResponse, UpdateStartOfCareDateApiResponse } from "../../../models/api/Referral";
import { createDocumentReviewPayload, DocumentReviewActionDispatchTypes, UpdateStartOfCareDateActionDispatchTypes} from "../../../constants/Referral";
import { UserPageTrack } from "../../../models/User";
import { redirectUri } from "../../../constants/Config";
import NoMncModal from "../containers/NoMncModal";
import NoSocModal from "./NoSoCModal";
import AuthNotificationsList from "./AuthNotificationsList";

export interface PropsFromState {
  referralData: ReferralDetail[];
  referralAuthHistory: ReferralAuthHistory[];
  showAuthHistory: number | null;
  setShowAuthHistory: (showAuthHistory: number | null) => void;
  providerId: number;
  documentReviewListData: DocumentReviewList;
  documentDownloadData: DocumentDownloadResponse;
  authNotificationsListState: AuthNotificationsListResponse;
  documentReviewListDataLoading: boolean;
  updateStartOfCareDateState: UpdateStartOfCareDateApiResponse;
  maxAllowedSocDateState: MaxAllowedSocDate | null;
  authNotificationsDownloadData: AuthNotificationsDownloadResponse;
}

export interface PropsFromDispatch {
  getReferralAuthHistory: (referralId: number) => void;
  resetStates: (actionType: string) => void;
  setDefaultInfoForReAuth: (payload: ReferralDetail) => void;
  postDocumentReview: (payload: DocumentReviewPayload) => void;
  documentDownload: (blobId: number) => void;
  postUserPageTrack: (payload: UserPageTrack) => void;
  updateSoC: (payload: UpdateStartOfCareDatePayload) => void;
  getMaxAllowedSocDate: (payload: MaxAllowedSocDatePayload) => void;
  postAuthNotifications: (referralId: number) => void;
  authNotificationsDownload: (blobId: number) => void;
}

type AllProps = PropsFromState & PropsFromDispatch;

const AuthorizationDetails: React.FC<AllProps> = ({
  referralData,
  getReferralAuthHistory,
  referralAuthHistory,
  showAuthHistory,
  setShowAuthHistory,
  resetStates,
  setDefaultInfoForReAuth,
  providerId,
  postDocumentReview,
  documentReviewListData,
  documentDownload,
  documentDownloadData,
  authNotificationsListState,
  documentReviewListDataLoading,
  updateStartOfCareDateState,
  postUserPageTrack,
  updateSoC,
  getMaxAllowedSocDate,
  maxAllowedSocDateState,
  postAuthNotifications,
  authNotificationsDownload,
  authNotificationsDownloadData
}) => {
  const navigate = useNavigate();
  const [referralAuthHistoryList, setReferralAuthHistoryList] = useState<
    ReferralAuthHistory[]
  >([]);
  const [isOpenNtuPatientModal, setIsOpenNtuPatientModal] =
    useState<boolean>(false);
  const [referralInfo, setReferralInfo] =
    useState<ReferralDetail>(defaultReferralInfo);
  const [isUnavailabilityModalOpen, setIsUnavailabilityModalOpen] = useState(false);
  const { response: documentDownloadResponse, error: documentDownloadError } = documentDownloadData;
  const [fileName, setFileName] = useState("");
  const [openPdfModal, setOpenPdfModal] = useState(false);
  const [selectedServiceRequestId, setSelectedServiceRequestId] = useState<number | null>(null);
  const [DownloadedDocName, setDownloadedDocName] = useState("");
  const [isOpenNoMncModal, setIsOpenNoMncModal] = useState<boolean>(false);
  const [isOpenSoCModal, setIsOpenSoCModal] = useState<boolean>(false);
  const [updatedSoCDatesByReferralId, setUpdatedSoCDatesByReferralId] = useState<Record<number, string>>({});
  const [updatedSoCSourcesByReferralId, setUpdatedSoCSourcesByReferralId] = useState<Record<number, string>>({});
  const [updatedEffectiveFromByReferralId, setUpdatedEffectiveFromByReferralId] = useState<Record<number, string>>({});
  const [updatedEffectiveThroughByReferralId, setUpdatedEffectiveThroughByReferralId] = useState<Record<number, string>>({});
  const [pendingSoCUpdateReferralId, setPendingSoCUpdateReferralId] = useState<number | null>(null);
  const { response: authNotificationsDownloadResponse, error: authNotificationsDownloadError } = authNotificationsDownloadData;
  const [authNotificationFileName, setAuthNotificationFileName] = useState("");
  const [AuthNotificationDownloadedDocName, setAuthNotificationDownloadedDocName] = useState("");

  useEffect(() => {
    setReferralAuthHistoryList(referralAuthHistory);
  }, [referralAuthHistory]);

  useEffect(() => {
    const updatedStartOfCareDate = getValue(
      updateStartOfCareDateState,
      "response.updatedStartOfCareDate",
      ""
    );
    const updatedSoCSourceName =
      getValue(updateStartOfCareDateState, "response.socSourceName", "")
    const updatedEffectiveFrom = getValue(
      updateStartOfCareDateState,
      "response.minEffectiveFromDate",
      ""
    );
    const updatedEffectiveThrough = getValue(
      updateStartOfCareDateState,
      "response.maxEffectiveThroughDate",
      ""
    );

    if (pendingSoCUpdateReferralId === null || updateStartOfCareDateState.loading) {
      return;
    }

    if (updateStartOfCareDateState.error) {
      setPendingSoCUpdateReferralId(null);
      return;
    }

     if (!updatedStartOfCareDate) {
      return;
    }

    setUpdatedSoCDatesByReferralId((prevState) => ({
      ...prevState,
      [pendingSoCUpdateReferralId]: updatedStartOfCareDate,
    }));

    if (updatedSoCSourceName) {
      setUpdatedSoCSourcesByReferralId((prevState) => ({
        ...prevState,
        [pendingSoCUpdateReferralId]: updatedSoCSourceName,
      }));
    }

    if (updatedEffectiveFrom) {
      setUpdatedEffectiveFromByReferralId((prevState) => ({
        ...prevState,
        [pendingSoCUpdateReferralId]: updatedEffectiveFrom,
      }));
    }

    if (updatedEffectiveThrough) {
      setUpdatedEffectiveThroughByReferralId((prevState) => ({
        ...prevState,
        [pendingSoCUpdateReferralId]: updatedEffectiveThrough,
      }));
    }
    
    if (referralInfo.referralId === pendingSoCUpdateReferralId) {
      setReferralInfo((prevState) => ({
        ...prevState,
        startOfCareDate: updatedStartOfCareDate,
      }));
    }

    setPendingSoCUpdateReferralId(null);
    setIsOpenSoCModal(false);
    resetStates(UpdateStartOfCareDateActionDispatchTypes.UPDATE_START_OF_CARE_DATE_RESET);
  }, [
    updateStartOfCareDateState,
    pendingSoCUpdateReferralId,
    referralInfo.referralId,
  ]);

  const handleUpdateSoC = (payload: UpdateStartOfCareDatePayload) => {
    if (updateStartOfCareDateState.loading) {
       return;
     }
    setPendingSoCUpdateReferralId(payload.referralId);
    resetStates(UpdateStartOfCareDateActionDispatchTypes.UPDATE_START_OF_CARE_DATE_RESET);
    updateSoC(payload);
  };

  useEffect(() => {
    resetStates(ReAuthDefaultValueActionDispatchTypes.RE_AUTH_DEFAULT_INFO_ELIGIBILITY_CHECK_RESET);
  }, []);

  const totalDisciplines = (referral: ReferralDetail) => {
    return (
      (referral.sn || 0) +
      (referral.pt || 0) +
      (referral.ot || 0) +
      (referral.st || 0) +
      (referral.msw || 0) +
      (referral.hha || 0)
    );
  };

  const getSoCSourceValue = (referral: ReferralDetail) => {
    const sourceFromReferral =
      referral.socSourceName ?? getValue(referral, "socSourceName", "");
    if (sourceFromReferral) {
      return sourceFromReferral;
    }

    return updatedSoCSourcesByReferralId[referral.referralId] ?? "";
  };

  const handleViewAuthHistory = (referralId: number) => {
    setReferralAuthHistoryList([]);
    resetStates(
      GetReferralAuthHistoryActionDispatchTypes.GET_REFERRAL_AUTH_HISTORY_RESET
    );
    if (showAuthHistory === referralId) {
      setShowAuthHistory(null);
    } else {
      getReferralAuthHistory(referralId);
      setShowAuthHistory(referralId);
    }
  };

  const tableCell = (value: ReactNode) => {
    return <TableCell>{renderDisciplineData(value)}</TableCell>;
  };

  const navigateToReAuthForm = (data: ReferralDetail) => {
    setDefaultInfoForReAuth({ ...data, providerId: providerId });
      const actualReferralId = data.referralId
        ? !isEmpty(getSessionStorage("samlToken")) ? encryptId(data.referralId) : data.referralId
        : null;
    
      const actualProviderId = providerId
        ? !isEmpty(getSessionStorage("samlToken")) ? encryptId(providerId) : providerId
        : null;
    navigate(`${ApplicationRoute.RE_AUTH_MEMBER_INFO}/${actualReferralId}/${actualProviderId}`)
  }

  const handleReAuthClick = (referral: ReferralDetail) => {
    if (referral.referralStatusName === "Active") {
      navigateToReAuthForm(referral);
    } else {
      setIsUnavailabilityModalOpen(true);
    }
  };
  
  const handleCloseUnavailabilityModal = () => {
    setIsUnavailabilityModalOpen(false);
  };

  const handleOpenPdfModal = (referralId: number, serviceRequestId: number) => {
    resetStates(DocumentReviewActionDispatchTypes.DOCUMENT_REVIEW_RESET);
    const Payload = createDocumentReviewPayload(referralId);
    postDocumentReview(Payload);
    setSelectedServiceRequestId(serviceRequestId);
    setOpenPdfModal(true);
  };

  const handleClosePdfModal = () => {
    setOpenPdfModal(false);
  };

  const downloadFile = (blobId: number, fileName: string) => {
    documentDownload(blobId);
    setFileName(fileName);
    setDownloadedDocName(fileName);
  };

  useEffect(() => {
    if (documentDownloadResponse && documentDownloadResponse.blob) {
      const blob = new Blob([documentDownloadResponse.blob], {
        type: documentDownloadResponse.headers["content-type"],
      });
      saveAs(blob, fileName, { autoBom: false });

      if (DownloadedDocName) {
        postUserPageTrack(
          getUaserPageViewPayload(
            getValidMessageType(ApplicationTitles.DOCUMENT_DOWNLOAD),
            getCurrnetPageFullPath(),
            redirectUri,
            DownloadedDocName
          )
        );
        setDownloadedDocName("");
      }
      setFileName("");
    } else if (
        documentDownloadError &&
        documentDownloadError.error &&
        Object.keys(documentDownloadError.error).length === 0
    ) {
        toastType.error(ResponseMessages(MessageType.DOCUMENT_DOWNLOAD_ERROR));
    }
  }, [documentDownloadResponse, documentDownloadError]);

  const authNotificationsDownloadFile = (blobId: number, fileName: string) => {
    authNotificationsDownload(blobId);
    setAuthNotificationFileName(fileName);
    setAuthNotificationDownloadedDocName(fileName);
  };

  useEffect(() => {
    if (authNotificationsDownloadResponse && authNotificationsDownloadResponse.blob) {
      const blob = new Blob([authNotificationsDownloadResponse.blob], {
        type: authNotificationsDownloadResponse.headers["content-type"],
      });
      saveAs(blob, authNotificationFileName, { autoBom: false });

      if (AuthNotificationDownloadedDocName) {
        postUserPageTrack(
          getUaserPageViewPayload(
            getValidMessageType(ApplicationTitles.DOCUMENT_DOWNLOAD),
            getCurrnetPageFullPath(),
            redirectUri,
            AuthNotificationDownloadedDocName
          )
        );
        setAuthNotificationDownloadedDocName("");
      }
      setAuthNotificationFileName("");
    } else if (
        authNotificationsDownloadError &&
        authNotificationsDownloadError.error &&
        Object.keys(authNotificationsDownloadError.error).length === 0
    ) {
        toastType.error(ResponseMessages(MessageType.DOCUMENT_DOWNLOAD_ERROR));
    }
  }, [authNotificationsDownloadResponse, authNotificationsDownloadError]);

  return (
    <>
      {referralData.map((referral: ReferralDetail) => (
        <Grid size={{ xs: 12 }} key={referral.referralId}>
          <Box sx={AuthDetailsBox(referral.referralStatusName === "Active")}>
            <Grid container sx={AuthDetailsGrid}>
              <Grid size={{ xs: 2 }}>
                <Box>
                  <Typography
                    variant="h3"
                    fontWeight={fontWeight[6]}
                    color={colors.portalColor[2]}
                    sx={ReferralName}
                  >
                    {`${referral.lastName}, ${referral.firstName}`}
                  </Typography>
                  {renderPatientDetails(
                    "DOB",
                    formatDate(referral.dateOfBirth, DateFormats.MM_DD_YYYY)
                  )}
                  {renderPatientDetails("Gender", referral.gender)}
                  {renderPatientDetails(
                    "MBI",
                    referral.medicareBeneficiaryIdentifier
                  )}
                  <Grid pt={"3.3rem"}>
                    {renderPatientDetails("Insurance", referral.benefitPlan)}
                    {renderPatientDetails(
                      "SubscriberID",
                      referral.subscriberId
                    )}
                  </Grid>
                </Box>
              </Grid>
              <Grid size={{ xs: 10 }}>
                <Grid container>
                  <Grid size={{ xs: 3.6 }}>
                    {renderPatientDetails(
                      "Status",
                      referral.referralStatusName,
                      true
                    )}
                    {renderPatientDetails(
                      "Authorization Type",
                      referral.authorizationTypeName,
                      true
                    )}
                    {renderPatientDetails(
                      "Contract Type",
                      referral.reimbursementTypeName,
                      true
                    )}
                  </Grid>
                  <Grid size={{ xs: 4 }}>
                    {renderPatientDetails(
                      "Auth#",
                      referral.authorizationNumber,
                      true
                    )}
                    {renderPatientDetails(
                      "Effective From - Through",
                      `${formatDate(
                        updatedEffectiveFromByReferralId[referral.referralId] ??
                          referral.minEffectiveFromDate,
                        DateFormats.MM_DD_YYYY
                      )} - ${formatDate(
                        updatedEffectiveThroughByReferralId[referral.referralId] ??
                          referral.maxEffectiveThroughDate,
                          DateFormats.MM_DD_YYYY
                      )}`,
                      true,
                    )}

                    <Grid container>
                      <Grid size={{ xs: 6 }}>
                        {renderPatientDetails(
                          "SoC Date",
                          formatDate(
                            updatedSoCDatesByReferralId[referral.referralId] ??
                              getValue(referral,"startOfCareDate") ??
                              "",
                            DateFormats.MM_DD_YYYY
                          ),
                          false,
                          colors.black[1],
                        )}
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        {renderPatientDetails(
                          "SoC Source",
                          getSoCSourceValue(referral),
                          false,
                          colors.black[1]
                        )}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid size={{ xs: 4.4 }}>
                    <Grid container>
                      <Grid size={{ xs: 12 }} pb={2}>
                        <Grid container spacing={1} alignItems="center" sx= {displayButton}>
                          <Button
                            variant="contained"
                            sx={ReAuthButton(referral.referralStatusName)}
                            fullWidth
                            onClick={() => handleReAuthClick(referral)}
                          >
                            Re-Auth
                          </Button>
                          {!referral.isReAuthCreated && (

                            <Button
                              sx={NtuButton}
                              fullWidth
                              onClick={() => {
                                setIsOpenNtuPatientModal(true);
                                setReferralInfo(referral);
                              }}
                            >
                              NTU this Patient
                            </Button>

                          )}

                          {!referral.isReAuthCreated && (

                            <Button
                              sx={SocButton}
                              fullWidth
                              onClick={() => {
                                getMaxAllowedSocDate({ referralId: referral.referralId });
                                resetStates(UpdateStartOfCareDateActionDispatchTypes.UPDATE_START_OF_CARE_DATE_RESET);
                                setIsOpenSoCModal(true);
                                setReferralInfo({
                                  ...referral,
                                  startOfCareDate:
                                    updatedSoCDatesByReferralId[referral.referralId] ??
                                    getValue(referral, "startOfCareDate"),
                                });
                              }}
                            >
                              Update SoC
                            </Button>

                          )}

                          <AuthNotificationsList
                            referral={referral}
                            authNotificationsListState={authNotificationsListState}
                            postAuthNotifications={postAuthNotifications}
                            authNotificationsFileDownload={(blobId: number,fileName?: string) => {
                              authNotificationsDownloadFile(blobId, fileName || "");
                            }}
                          />

                          <Button
                            sx={NoMnCButton}
                            fullWidth
                            onClick={() => {
                              setIsOpenNoMncModal(true);
                              setReferralInfo(referral);
                            }}
                          >
                            Upload NOMNC
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid size={{ xs: 12 }} pb={2}>
                      <Grid container>
                        {renderGridItem("Totals", 1.5)}
                        {renderGridItem("SN", 1.1, referral.sn, DisciplineGrid)}
                        {renderGridItem("PT", 1.1, referral.pt, DisciplineGrid)}
                        {renderGridItem("OT", 1.1, referral.ot, DisciplineGrid)}
                        {renderGridItem("ST", 1.1, referral.st, DisciplineGrid)}
                        {renderGridItem(
                          "MSW",
                          1.7,
                          referral.msw,
                          DisciplineGrid
                        )}
                        {renderGridItem(
                          "HHA",
                          1.3,
                          referral.hha,
                          DisciplineGrid
                        )}
                        {renderGridItem(
                          "Total",
                          2,
                          totalDisciplines(referral),
                          TotalDisciplineGrid
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={{ xs: 12 }} sx={ViewAuthHistoryGrid}>
                    <Button
                      sx={ViewAuthHistoryButton}
                      onClick={() => handleViewAuthHistory(referral.referralId)}
                      disableRipple
                    >
                      View Auth History
                    </Button>
                    {showAuthHistory === referral.referralId &&
                      referralAuthHistoryList &&
                      getValue(referralAuthHistoryList, "[0].referralId") >
                        0 && (
                        <TableContainer
                          component={Paper}
                          sx={TableContainerStyle}
                        >
                          <Table>
                            <TableHead>
                              <TableRow>
                                {AuthHistoryHeaderLabel.map((data: any) => (
                                  <TableCell
                                    key={data.label}
                                    sx={HeaderTableCell}
                                  >
                                    <Box sx={data.style?.cell}>
                                      <Typography
                                        color={colors.portalColor[2]}
                                        variant="h4"
                                        fontWeight={fontWeight[6]}
                                      >
                                        {data.label}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {referralAuthHistoryList.map(
                                (data: ReferralAuthHistory) => {
                                  return (
                                    <StyledTableRow key={data.referralId}>
                                      {tableCell(
                                        formatDate(
                                          data.receivedDateTime,
                                          DateFormats.MM_DD_YYYY
                                        )
                                      )}
                                      {tableCell(data.status)}
                                      {tableCell(
                                        `${formatDate(
                                          data.minEffectiveFromDate,
                                          DateFormats.MM_DD_YYYY
                                        )} - ${formatDate(
                                          data.maxEffectiveThroughDate,
                                          DateFormats.MM_DD_YYYY
                                        )}`
                                      )}
                                      {tableCell(
                                        <Box
                                          sx={{
                                            cursor: "pointer",
                                            paddingLeft: "3rem",
                                          }}
                                          onClick={() =>
                                            handleOpenPdfModal(
                                              data.referralId,
                                              data.serviceRequestId
                                            )
                                          }
                                        >
                                          <FaRegFilePdfIcon />
                                        </Box>
                                      )}
                                      {tableCell(data.sn)}
                                      {tableCell(data.pt)}
                                      {tableCell(data.ot)}
                                      {tableCell(data.st)}
                                      {tableCell(data.msw)}
                                      {tableCell(data.hha)}
                                    </StyledTableRow>
                                  );
                                }
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      ))}
      {isOpenNtuPatientModal && (
        <NtuPatient
          isOpenModal={isOpenNtuPatientModal}
          setIsOpenModal={setIsOpenNtuPatientModal}
          referralInfo={referralInfo}
        />
      )}
      {isUnavailabilityModalOpen && (
        <UnavailabilityModal
          isOpenModal={isUnavailabilityModalOpen}
          handleClose={handleCloseUnavailabilityModal}
          errorMessage={[REAUTH_UNAVAILIBILITY_ERROR_MESSAGE]}
        />
      )}
      {isOpenNoMncModal && (
        <NoMncModal
          referralId={referralInfo.referralId}
          nOMNModalFlag={isOpenNoMncModal}
          setNOMNCModalflag={setIsOpenNoMncModal}
        />
      )}
      {isOpenSoCModal && (
        <NoSocModal
          SoCModalFlag={isOpenSoCModal}
          currentStartOfCare={
            updatedSoCDatesByReferralId[referralInfo.referralId] ??
            getValue(referralInfo, "startOfCareDate") ??
            ""
          }
          healthPlan={referralInfo.benefitPlan ?? ""}
          setSoCModalFlag={setIsOpenSoCModal}
          referralId={referralInfo.referralId}
          updateSoC={handleUpdateSoC}
          maxAllowedSocDateState={maxAllowedSocDateState}
          updateStartOfCareDateState={updateStartOfCareDateState}
        />
      )}
      <DocumentModal
        isOpenModal={openPdfModal}
        handleClose={handleClosePdfModal}
        documents={(documentReviewListData?.blobDocumentList || []).filter(
          (doc) => doc.serviceRequestId === selectedServiceRequestId
        )}
        onDocumentClick={(blobId: number, fileName?: string) => {
          downloadFile(blobId, fileName || "");
        }}
        isLoading={documentReviewListDataLoading}
      />
    </>
  );
};

const renderPatientDetails = (
  label: string,
  value: string,
  isBold?: boolean,
  labelColor?: string
) => {
  return (
    <Box pb={isBold ? "0.6rem" : "0.3rem"} sx={displayFlex} alignItems={"center"}>
      <Typography
        variant={isBold ? "h3" : "h5"}
        fontWeight={isBold ? fontWeight[6] : fontWeight[5]}
        color={labelColor ?? (isBold ? colors.portalColor[2] : colors.portalColor[3])}
      >
        {label}:
      </Typography>
      <Typography
        variant="h5"
        fontWeight={fontWeight[2]}
        color={colors.portalColor[2]}
        pl={"0.3rem"}
        pt={isBold ? "0.1rem" : "0rem"}
      >
        {value}
      </Typography>
    </Box>
  );
};

const renderDisciplineData = (value: ReactNode) => {
  return (
    <Typography
      variant="h4"
      fontWeight={fontWeight[2]}
      color={colors.portalColor[2]}
      pt={1}
    >
      {value}
    </Typography>
  );
};

const renderGridItem = (
  label: string,
  size: number,
  value?: string | number,
  style?: object
) => (
  <Grid key={label} size={{ xs: size }} sx={style}>
    {renderDisciplineData(label)}
    {value !== undefined && renderDisciplineData(value)}
  </Grid>
);

interface UnavailabilityModalProps {
  isOpenModal: boolean;
  handleClose: () => void;
  errorMessage: string[];
}
const UnavailabilityModal: React.FC<UnavailabilityModalProps> = ({
  isOpenModal,
  handleClose,
  errorMessage,
}) => {

  const redirectFreshDesk = () => {
    window.open(ApplicationURL.FRESH_DESK, "_blank");
  };

  return (
    <Modal
      open={isOpenModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={flexAllCentre}
    >
      <Grid container sx={UnavailibilityContainer}>
        <Grid size={{ xs: 12 }}>
          {errorMessage.map((Message) => (
            <Box key={nanoid()} sx={displayFlex} pb={"0.7rem"}>
              <FiAlertCircleIcon />
              <Typography
                variant="subtitle1"
                color={colors.red[100]}
                fontWeight={fontWeight[4]}
                sx={UnavailabilityMessage}
              >
                {Message}
              </Typography>
            </Box>
          ))}
        </Grid>
        <Grid size={{ xs: 12 }} pr={"1rem"}>
          <Box sx={displayFlexEnd}>
              <IconButton onClick={redirectFreshDesk} sx={{padding: "0rem"}}>
                <FaHeadsetIcon />
              </IconButton>
            <ButtonContained
              id="btnClose"
              onClick={handleClose}
              label="Close"
              style={CloseButton}
            />
          </Box>
        </Grid>
      </Grid>
    </Modal>
  );
};

interface DocumentModalProps {
  isOpenModal: boolean;
  handleClose: () => void;
  documents: BlobDocument[];
  onDocumentClick: (blobId: number, originalDocumentName: string) => void;
  isLoading?: boolean;
}

const DocumentModal: React.FC<DocumentModalProps> = ({
  isOpenModal,
  handleClose,
  documents,
  onDocumentClick,
  isLoading = false,
}) => {
  const filteredDocuments = documents.filter((doc) =>
    supportedFileTypes.includes(doc.mediaType)
  );

  return (
    <Modal
      open={isOpenModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={flexAllCentre}
    >
      <Grid container sx={DocumentContainer}>
        <Grid size={{ xs: 12 }} pb={"1rem"}>
          <Typography
            variant="h2"
            color={colors.black[1]}
            fontWeight={fontWeight[4]}
          >
            Uploaded documents attached during request
          </Typography>
        </Grid>

        <Grid size={{ xs: 12 }}>
          {isLoading && (
            <Typography
              variant="h4"
              color={colors.portalColor[2]}
              fontWeight={fontWeight[3]}
            >
              Loading Documents...
            </Typography>
          )}
          {!isLoading && filteredDocuments.length > 0 && (
            <TableContainer component={Paper} sx={DocumentTableContainerStyle}>
              <Table stickyHeader aria-label="documents table">
                <TableHead>
                  <TableRow>
                    {DocumentReviewHeaderLabel.map((data: any) => (
                      <TableCell
                        key={data.label}
                        sx={DocumentTableHead}
                      >
                        <Box sx={data.style?.cell}>
                          <Typography
                            color={colors.portalColor[2]}
                            variant="h4"
                            fontWeight={fontWeight[6]}
                          >
                            {data.label}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                    {filteredDocuments.map((doc, index) => {
                      return (
                        <TableRow key={doc.referralBlobId || index} sx={DocumentModalTableRow}>
                          <TableCell>
                            <Link
                              variant="h5"
                              fontWeight={fontWeight[4]}
                              color={colors.fonts[18]}
                              onClick={() => onDocumentClick(doc.blobId, doc.originalDocumentName)}
                              sx={DocumentNameUnderline}
                            >
                              {doc.originalDocumentName}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="subtitle1"
                              fontWeight={fontWeight[5]}
                              color={colors.fonts[4]}
                            >
                              {bytesToKB(doc.blobContentLength)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="subtitle1"
                              fontWeight={fontWeight[5]}
                              color={colors.fonts[4]}
                            >
                              {formatDate(doc.dateTimeReceived, DateFormats.MM_DD_YYYY_comma_hh_mm_ss_a)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          )} 
          {!isLoading && filteredDocuments.length === 0 && (
            <Typography 
              variant="h4"
              color={colors.portalColor[2]}
              fontWeight={fontWeight[3]}
            >
              No Documents Found.
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 12 }} pr={"1rem"} pt={"1rem"} sx={CloseButtonGrid}>
          <Box sx={displayFlexEnd}>
            <ButtonContained
              id="btnClose"
              onClick={handleClose}
              label="Close"
              style={CloseButton}
            />
          </Box>
        </Grid>
      </Grid>
    </Modal>
  );
};

export default AuthorizationDetails;
